import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { acesso } from '../api/acesso'
import { ErroApi } from '../api/client'
import { mensagemDeErro } from './mensagemDeErro'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { useSessao } from '../auth/useSessao'
import './acesso.css'

type Etapa = { nome: 'credencial' } | { nome: 'segundoFator'; referencia: string }

/**
 * Caminhos que existem ANTES da sessão. Entrar por um deles é o caso em que a
 * tela de login precisa navegar sozinha depois de autenticar.
 */
const ANONIMOS = ['/entrar', '/esqueci-a-senha', '/definir-senha', '/redefinir-senha']

/**
 * Para onde ir depois de entrar.
 *
 * 🔴 Esta função existe porque a tela de login vive em dois contextos, e eles
 * pedem coisas opostas:
 *
 * - Renderizada pela **guarda** (`Protegido`) dentro de `/usuarios`, `/painel`
 *   ou qualquer tela protegida: **não navega**. Assim que a sessão é definida, a
 *   guarda troca o conteúdo no lugar — e navegar aqui **perderia o destino** que
 *   a pessoa tinha pedido.
 * - Aberta direto em **`/entrar`**: precisa navegar. O caminho continua sendo
 *   `/entrar`, o roteador continua devolvendo a tela de login, e sem isto a
 *   pessoa autentica com sucesso e **fica olhando o mesmo formulário** — que foi
 *   exatamente o que aconteceu em 05/08.
 *
 * O `?destino=` é aceito para o convite por e-mail poder levar direto à tela
 * pedida, mas **só caminho relativo do próprio portal**: qualquer coisa que
 * comece com `//` ou tenha esquema é redirecionamento aberto disfarçado de
 * conveniência.
 */
function destinoDepoisDeEntrar(): string | null {
  if (!ANONIMOS.includes(window.location.pathname)) return null

  const pedido = new URLSearchParams(window.location.search).get('destino')

  if (pedido && pedido.startsWith('/') && !pedido.startsWith('//')) return pedido

  return '/'
}

/**
 * Entrada em duas etapas.
 *
 * A segunda etapa só aparece quando o servidor diz que ela é necessária — o
 * front não decide isso a partir do perfil, porque ele nem sabe o perfil antes
 * de autenticar, e é justamente esse o ponto.
 */
export function Entrar({ esqueciEmNovaAba = false }: { esqueciEmNovaAba?: boolean } = {}) {
  const { t } = useTranslation()
  const { definir } = useSessao()

  const [etapa, setEtapa] = useState<Etapa>({ nome: 'credencial' })
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [codigo, setCodigo] = useState('')
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  /**
   * Sai da tela de login quando ela foi aberta por caminho próprio.
   *
   * `assign` e não `replace`: a tela de login **deve** ficar no histórico, para
   * o botão "voltar" não devolver a pessoa a um formulário que já cumpriu o
   * papel — e, se ela voltar mesmo assim, entrar de novo simplesmente funciona.
   */
  function seguir() {
    const destino = destinoDepoisDeEntrar()

    if (destino) window.location.assign(destino)
  }

  async function entrar(evento: FormEvent) {
    evento.preventDefault()
    setErro(null)
    setEnviando(true)

    try {
      const resposta = await acesso.entrar(email, senha)

      if (resposta.segundoFator && resposta.referencia) {
        setEtapa({ nome: 'segundoFator', referencia: resposta.referencia })
        return
      }

      if (resposta.usuario) {
        definir(resposta.usuario)
        seguir()
      }
    } catch (e) {
      setErro(mensagemDeErro(e, t))
    } finally {
      setEnviando(false)
    }
  }

  async function confirmar(evento: FormEvent) {
    evento.preventDefault()
    if (etapa.nome !== 'segundoFator') return

    setErro(null)
    setEnviando(true)

    try {
      const { usuario } = await acesso.segundoFator(etapa.referencia, codigo)

      definir(usuario)
      seguir()
    } catch (e) {
      setErro(mensagemDeErro(e, t))

      // Código expirado devolve o usuário ao começo: insistir num desafio
      // morto só produz mais erro.
      if (e instanceof ErroApi && e.status === 400) {
        setEtapa({ nome: 'credencial' })
        setSenha('')
        setCodigo('')
      }
    } finally {
      setEnviando(false)
    }
  }

  return (
    <main className="acesso">
      <div className="acesso__cartao">
        <h1 className="acesso__titulo">{t('acesso.titulo')}</h1>

        {etapa.nome === 'credencial' ? (
          <form onSubmit={entrar} noValidate>
            <p className="acesso__descricao">{t('acesso.descricao')}</p>

            {erro && <Aviso tom="perigo">{erro}</Aviso>}

            <Campo
              rotulo={t('acesso.email')}
              type="email"
              name="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              obrigatorio
              autoFocus
            />

            <Campo
              rotulo={t('acesso.senha')}
              type="password"
              name="senha"
              autoComplete="current-password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              obrigatorio
            />

            <div className="acesso__acoes">
              <Botao type="submit" variante="primario" carregando={enviando}>
                {t('acesso.entrar')}
              </Botao>

              {/* No painel do Chatwoot (282), o link não pode levar o iframe embora: a
                  redefinição terminaria com o portal inteiro dentro do Chatwoot. */}
              <a
                className="acesso__link"
                href="/esqueci-a-senha"
                {...(esqueciEmNovaAba ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              >
                {t('acesso.esqueci')}
              </a>
            </div>
          </form>
        ) : (
          <form onSubmit={confirmar} noValidate>
            <p className="acesso__descricao">{t('acesso.codigoEnviado')}</p>

            {erro && <Aviso tom="perigo">{erro}</Aviso>}

            <Campo
              rotulo={t('acesso.codigo')}
              name="codigo"
              // `one-time-code` faz o celular oferecer o código do e-mail
              // direto no teclado; sem isso o usuário alterna de aplicativo.
              autoComplete="one-time-code"
              inputMode="text"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              ajuda={t('acesso.codigoAjuda')}
              obrigatorio
              autoFocus
            />

            <div className="acesso__acoes">
              <Botao type="submit" variante="primario" carregando={enviando}>
                {t('acesso.confirmar')}
              </Botao>

              <Botao
                variante="discreto"
                onClick={() => {
                  setEtapa({ nome: 'credencial' })
                  setErro(null)
                  setCodigo('')
                }}
              >
                {t('comum.cancelar')}
              </Botao>
            </div>
          </form>
        )}
      </div>
    </main>
  )
}
